import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Calendar, Mail, Sparkles, Clock } from "lucide-react";
import confetti from "canvas-confetti";
import type { LeadFormData } from "../types";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";


interface Props {
  data: LeadFormData;
}

const STEPS = [
  {
    icon: Mail,
    title: "Check your inbox",
    body:
      "Calendar invite + confirmation email is on its way. Add it to your calendar so you don't miss it.",
  },
  {
    icon: Sparkles,
    title: "We get to work",
    body:
      "Our team starts crafting your custom mockup right now. You'll see it on the call.",
  },
  {
    icon: Calendar,
    title: "Show up ready",
    body:
      "Hop on the call at your booked time. We'll walk you through your mockup and a clear plan to launch.",
  },
];




export function StepBooked(_props: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);


  useEffect(() => {
    if (!canvasRef.current) return;
    const myConfetti = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: true,
    });
    const colors = ["#0eadff", "#00e7a8", "#f9cd39", "#ec4899", "#a855f7"];
    const end = Date.now() + 3500;
    const frame = () => {
      myConfetti({
        particleCount: 8,
        angle: 60,
        spread: 85,
        startVelocity: 70,
        scalar: 1.3,
        origin: { x: 0, y: 0.6 },
        colors,
      });
      myConfetti({
        particleCount: 8,
        angle: 120,
        spread: 85,
        startVelocity: 70,
        scalar: 1.3,
        origin: { x: 1, y: 0.6 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    myConfetti({
      particleCount: 220,
      spread: 140,
      startVelocity: 60,
      scalar: 1.4,
      origin: { y: 0.5 },
      colors,
    });
    frame();
    return () => {
      myConfetti.reset();
    };
  }, []);

  return (
    <div className="relative -mt-4 space-y-8">
      {typeof document !== "undefined" &&
        createPortal(
          <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
              position: "fixed",
              inset: 0,
              width: "100vw",
              height: "100vh",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />,
          document.body,
        )}
      <div className="space-y-4 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-foreground">
          <Clock className="h-3.5 w-3.5 text-brand-blue" />
          Call locked in
        </div>
        <h2 className="text-balance text-4xl font-bold tracking-tighter text-foreground sm:text-5xl">
          You're <span className="text-brand-blue">booked in.</span>
        </h2>
        <p className="mx-auto max-w-md text-base text-muted-foreground">
          Your free mockup strategy call is confirmed.
          <br />
          Here's exactly what happens next.
        </p>
      </div>

      {/* Mobile-only video between intro and steps */}
      <ShareloEmbed id="Pl7YMKbkCjXLVT5cKzMNN" title="Booked — what happens next" className="lg:hidden" />


      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.title}
              className="rounded-xl border border-border bg-background/50 p-4 text-center sm:text-left"
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue">
                <Icon className="h-7 w-7" />
              </div>
              <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Step {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-1 text-base font-semibold text-foreground">{s.title}</div>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

